import { HorizontalGaugeIndicatorProps } from './types'
import { twMerge } from 'tailwind-merge'
import { clamp } from '../../utilities'

const DEFAULT_MIN = -1
const DEFAULT_MAX = 1
const DEFAULT_WIDTH = 40
const DEFAULT_SNAP_POINTS = 3

const getSnapStops = (
  minValue: number,
  maxValue: number,
  snapPoints: number,
) => {
  const count = Math.max(2, Math.floor(snapPoints))
  const step = (maxValue - minValue) / (count - 1)

  return Array.from({ length: count }, (_, index) => minValue + step * index)
}

// Snap the indicator to the next stop moving from min -> max, evenly spaced.
const getSnappedValue = (
  value: number,
  minValue: number,
  maxValue: number,
  snapPoints: number,
) => {
  const stops = getSnapStops(
    minValue,
    maxValue,
    snapPoints || DEFAULT_SNAP_POINTS,
  )

  for (const stop of stops) {
    if (value <= stop) {
      return stop
    }
  }

  return stops[stops.length - 1]
}

const getOffset = (
  value: number,
  minValue: number,
  maxValue: number,
  width: number,
  snapToPoints: boolean,
  snapPoints: number,
) => {
  if (minValue === maxValue) {
    return `calc(50% - ${width / 2}px)`
  }

  const clampedValue = clamp(value, minValue, maxValue)
  const normalizedValue = snapToPoints
    ? getSnappedValue(clampedValue, minValue, maxValue, snapPoints)
    : clampedValue
  const percentage =
    ((normalizedValue - minValue) / (maxValue - minValue)) * 100 || 0
  const baseOffset = `calc(${percentage}% - ${width / 2}px)`

  return `min(calc(100% - ${width}px), max(0px, ${baseOffset}))`
}

const HorizontalGaugeIndicator: React.FC<HorizontalGaugeIndicatorProps> = ({
  value,
  minValue = DEFAULT_MIN,
  maxValue = DEFAULT_MAX,
  width = DEFAULT_WIDTH,
  snapToPoints = false,
  snapPoints = DEFAULT_SNAP_POINTS,
  className,
  style,
  ...props
}) => {
  const left = getOffset(
    value,
    minValue,
    maxValue,
    width,
    snapToPoints,
    snapPoints,
  )

  return (
    <div
      {...props}
      className={twMerge('absolute top-0 bg-black h-1 md:h-2', className)}
      style={{ width, left, ...style }}
    />
  )
}

export { HorizontalGaugeIndicator }
